import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Target, Info, PlayCircle, RotateCcw, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CaseStudyIntro() {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCaseDetails();
  }, [caseId]);

  const fetchCaseDetails = async () => {
    try {
      const res = await fetch(`${API}/api/case-studies/${caseId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch case details');
      const data = await res.json();
      setCaseStudy(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartCase = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API}/api/case-studies/${caseId}/start`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to start case');
      const data = await res.json();
      navigate(`/case-studies/${caseId}/session`, { state: { initialData: data } });
    } catch (err) {
      setError(err.message);
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading case details...</div>
      </div>
    );
  }

  if (error || !caseStudy) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="bg-white border-slate-200 max-w-md">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Error Loading Case</h3>
            <p className="text-slate-500 mb-4">{error || 'Case not found'}</p>
            <Button onClick={() => navigate('/case-studies')} variant="outline">
              Back to Case Studies
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/case-studies')}
            className="text-slate-600 hover:text-slate-900 mb-4"
            data-testid="back-to-cases"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Case Studies
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Case Title & Overview */}
        <div className="mb-8">
          <div className="flex items-start gap-3 mb-4">
            {caseStudy.existing_attempt && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                In Progress
              </Badge>
            )}
          </div>
          <h1 className="text-3xl font-bold mb-4 text-slate-900" data-testid="case-title">{caseStudy.title}</h1>
          <p className="text-lg text-slate-600">{caseStudy.overview}</p>
          
          {/* Topics */}
          {caseStudy.topic_keys?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {caseStudy.topic_keys.map((topic) => (
                <Badge
                  key={topic}
                  variant="outline"
                  className="bg-purple-50 text-purple-700 border-purple-200"
                >
                  {topic}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Learning Objectives */}
        <Card className="bg-white border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <Target className="w-5 h-5" />
              Learning Objectives
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {caseStudy.learning_objectives.map((objective, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <span className="text-slate-700">{objective}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Scoring Framework */}
        <Card className="bg-white border-slate-200 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Info className="w-5 h-5" />
              How You&apos;ll Be Evaluated
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 whitespace-pre-wrap">{caseStudy.scoring_framework_text}</p>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-600">
                <strong className="text-slate-700">Note:</strong> During the case, you will only see &quot;Answer saved&quot; after each response. 
                Your score, correct answers, and detailed clinical feedback will be revealed only at the end.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Case Info */}
        <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-slate-200 mb-8">
          <div className="text-slate-600">
            <span className="text-slate-900 font-medium">{caseStudy.stage_count}</span> stages in this case
          </div>
          {caseStudy.existing_attempt && (
            <div className="text-amber-600 text-sm">
              You have an in-progress attempt. Clicking &quot;Resume&quot; will continue where you left off.
            </div>
          )}
        </div>

        {/* Start/Resume Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleStartCase}
            disabled={starting}
            className={`px-8 py-6 text-lg ${
              caseStudy.existing_attempt
                ? 'bg-amber-500 hover:bg-amber-600'
                : 'bg-purple-600 hover:bg-purple-700'
            } text-white`}
            data-testid="start-case-btn"
          >
            {starting ? (
              'Loading...'
            ) : caseStudy.existing_attempt ? (
              <>
                <RotateCcw className="w-5 h-5 mr-2" />
                Resume Case
              </>
            ) : (
              <>
                <PlayCircle className="w-5 h-5 mr-2" />
                Begin Case Study
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
